import { useState } from "react";
import { useParams } from "react-router-dom";

import { useConfirmSelections } from "hooks/useArrangementAPI";
import { ApiErrorCode, apiErrorCode, getApiErrorMessage } from "utils/errors";

import { useArrangement } from "./ArrangementContext";
import ArrangementNav from "./ArrangementNav";
import ConfirmationEventDisplay from "./ConfirmationEventDisplay";
import { STEP_ROUTES } from "./constants";
import type { ResolvedConfirmationEvent } from "./types";

/**
 * Footer for the review (confirm-selections) screen. Both states run a single
 * confirm-and-advance action: the unconfirmed "Confirm & Continue" button and
 * the confirmed-return "Continue" button each POST /confirm-selections, then
 * advance to signing. The confirmed state additionally shows who confirmed.
 */
export default function ReviewActions({
  confirmedBy,
  onBack,
  onContinue,
  revalidate,
}: {
  confirmedBy: ResolvedConfirmationEvent | null;
  onBack: () => void;
  onContinue: () => void;
  revalidate: () => Promise<unknown>;
}) {
  const { caseId } = useParams<{ caseId: string }>();
  const { flushSave, goToStep } = useArrangement();
  const { trigger: confirmSelections, isMutating } =
    useConfirmSelections(caseId);
  const [error, setError] = useState<string | null>(null);
  const [needsDeathCertReview, setNeedsDeathCertReview] = useState(false);

  // Single confirm-and-advance action, used by both the unconfirmed
  // "Confirm Selections" button and the confirmed-state "Continue" button:
  // persist any pending edits, run the idempotent confirm (which records the
  // confirmation and kicks off document creation, reconciling an already- or
  // half-confirmed arrangement on the way through), then revalidate so the
  // sign screen's lifecycle guard sees the recorded confirmation before it
  // mounts, and advance. One click confirms and moves on — the caller never
  // has to press the button twice, and we never fire confirm-selections twice
  // (a second call would, in dev, kick a second creation task that voids and
  // recreates the document).
  const handleConfirmAndContinue = async () => {
    setError(null);
    try {
      await flushSave();
      await confirmSelections({});
      await revalidate();
      onContinue();
    } catch (err) {
      // What was reviewed no longer matches what the case records about the
      // death certificates, and the documents are built from the case. Send the
      // family back to that question rather than confirming either version.
      if (
        apiErrorCode(err) ===
        ApiErrorCode.Arrangement.DC_ORDERING_PREFERENCE_MISMATCH
      ) {
        setNeedsDeathCertReview(true);
        setError(
          getApiErrorMessage(
            ApiErrorCode.Arrangement.DC_ORDERING_PREFERENCE_MISMATCH,
          ),
        );
        return;
      }
      setError("We couldn't confirm your selections. Please try again.");
    }
  };

  // Services opens on the death certificate question because it is the first
  // one the step renders — the flow addresses steps, not individual questions,
  // so this branch depends on that ordering.
  const reviewDeathCertificates = () =>
    goToStep(STEP_ROUTES.indexOf("services"));

  const continueAction = needsDeathCertReview
    ? reviewDeathCertificates
    : handleConfirmAndContinue;

  // Forward-looking hint shown with the CTA so confirming never reads as an
  // endpoint. "Next:" is a hanging label; the body wraps in its own column
  // beside it rather than flowing back under the label.
  const breadcrumb = (
    <span className="flex items-baseline gap-1.5">
      <span className="shrink-0 font-semibold">Next:</span>
      <span>Review and sign your Statement of Funeral Goods and Services.</span>
    </span>
  );

  const continueHint = needsDeathCertReview ? undefined : breadcrumb;
  const continueLabel = needsDeathCertReview
    ? "Review death certificates"
    : isMutating
      ? "Confirming…"
      : confirmedBy
        ? "Continue"
        : "Confirm & Continue";

  if (confirmedBy) {
    return (
      <div className="mt-12 space-y-6">
        <div className="mx-auto max-w-lg">
          <ConfirmationEventDisplay event={confirmedBy} />
        </div>
        {error && (
          <p className="text-center text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <ArrangementNav
          onBack={onBack}
          onContinue={continueAction}
          continueLabel={continueLabel}
          continueHint={continueHint}
          disableContinue={isMutating}
        />
      </div>
    );
  }

  return (
    <div className="mt-12 space-y-3">
      {error && (
        <p className="text-center text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      <ArrangementNav
        onBack={onBack}
        onContinue={continueAction}
        continueLabel={continueLabel}
        continueHint={continueHint}
        disableContinue={isMutating}
      />
    </div>
  );
}
